<template>
  <div v-if="hasProperty" class="property-display-component" v-bind:class="{loaded}">
    <PropertyEditDrawerComponent v-if="edit" ref="editDrawer" v-bind:property="property"
                                 v-on:updated="handlePropertyUpdated"/>
    <table class="table table-fixed table-striped table-bordered">
      <tbody v-if="compact">
      <tr>
        <td colspan="4">
          <h4>Adresgegevens</h4>
        </td>
      </tr>
      <tr>
        <th>Postcode</th>
        <td>
          {{ property.postal_code }}
        </td>
        <th>Huisnummer</th>
        <td>
          {{ property.formatted_number }}
        </td>
      </tr>
      <tr>
        <th>Straat</th>
        <td>
          {{ property.street }}
        </td>
        <th>Stad</th>
        <td>
          {{ property.city }}
        </td>
      </tr>
      <tr>
        <th>Gemeente</th>
        <td>
          {{ property.municipality }}
        </td>
        <th>Provincie</th>
        <td>
          {{ property.province }}
        </td>
      </tr>
      <tr>
        <th>Land</th>
        <td>
          {{ countryName }}
        </td>
      </tr>
      </tbody>
      <tbody v-else>
      <tr>
        <td colspan="4">
          <h4>Adresgegevens</h4>
        </td>
      </tr>
      <tr>
        <th>Postcode</th>
        <td>
          {{ property.postal_code }}
        </td>
        <th>Huisnummer</th>
        <td>
          {{ property.formatted_number }}
        </td>
      </tr>
      <tr>
        <th>Straat</th>
        <td>
          {{ property.street }}
        </td>
        <th>Stad</th>
        <td>
          {{ property.city }}
        </td>
      </tr>
      <tr>
        <th>Gemeente</th>
        <td>
          {{ property.municipality }}
        </td>
        <th>Provincie</th>
        <td>
          {{ property.province }}
        </td>
      </tr>
      <tr>
        <th>Land</th>
        <td>
          {{ countryName }}
        </td>
      </tr>
      <tr>
        <td colspan="4">
          <h4>Kadastrale gegevens</h4>
        </td>
      </tr>
      <tr>
        <th>Kadestrale aanduiding</th>
        <td>
          {{ property.cadastral_designation }}
        </td>
        <th>Kadaster object id</th>
        <td>
          {{ property.cadastral_number }}
        </td>
      </tr>
      <tr>
        <th>Bouwjaar</th>
        <td>
          {{ property.construction_year }}
        </td>
        <th>Woning oppervlakte</th>
        <td>
          {{ property.size | toSurface }}
        </td>
      </tr>
      <tr>
        <th>Perceeloppervlakte</th>
        <td>
          {{ property.plot_size | toSurface }}
        </td>
        <th>Woning type</th>
        <td>
          {{ property.residential_building_type }}
        </td>
      </tr>
      <tr>
        <th>Woning subtype</th>
        <td>
          {{ property.residential_building_sub_type }}
        </td>
        <th>Gebruiksdoel</th>
        <td>
          {{ property.purpose }}
        </td>
      </tr>
      <tr>
        <th>Gebruiksdoel status</th>
        <td>
          {{ property.status }}
        </td>
        <th>Herbouwwaarde</th>
        <td>
          {{ property.rebuild_value | toCurrency(0) }}
        </td>
      </tr>
      <tr>
        <td colspan="4">
          <h4>Energie</h4>
        </td>
      </tr>
      <tr>
        <th>Energielabel</th>
        <td>
          {{ property.energy_label }}
        </td>
        <th>Einddatum energielabel</th>
        <td>
          {{ property.energy_label_end_date | toDate }}
        </td>
      </tr>
      <tr>
        <th>Stroom normaal</th>
        <td>
          {{ property.electricity_normal| toEnergy }}
        </td>
        <th>Stroom dal</th>
        <td>
          {{ property.electricity_low | toEnergy }}
        </td>
      </tr>
      <tr>
        <th>Dubbele of slimme meter</th>
        <td>
          {{ property.double_or_smart_meter  | toYesNo }}
        </td>
        <th>Zonnepanelen</th>
        <td>
          {{ property.solar_panels | toYesNo }}
        </td>
      </tr>
      <tr v-if="property.solar_panels">
        <th>Stroomopwekking normaal</th>
        <td>
          {{ property.electricity_generated_normal | toEnergy }}
        </td>
        <th>Stroomopwekking dal</th>
        <td>
          {{ property.electricity_generated_low | toEnergy }}
        </td>
      </tr>
      <tr>
        <th>Gas</th>
        <td>
          {{ property.gas | toVolume }}
        </td>
      </tr>
      <tr>
        <td colspan="4">
          <h4>Erfpacht</h4>
        </td>
      </tr>
      <tr>
        <th>Is erfpacht</th>
        <td>
          {{ property.is_leasehold | toYesNo }}
        </td>
        <th v-if="property.is_leasehold">Is erfpacht eeuwigdurend</th>
        <td v-if="property.is_leasehold">
          {{ property.is_leasehold_forever | toYesNo }}
        </td>
      </tr>
      <tr v-if="property.is_leasehold">
        <th>Erfpacht startdatum</th>
        <td>
          {{ property.leasehold_start_date | toDate }}
        </td>
        <th v-if="!property.is_leasehold_forever">Erfpacht einddatum</th>
        <td v-if="!property.is_leasehold_forever">
          {{ property.leasehold_end_date | toDate }}
        </td>
      </tr>
      <tr v-if="property.is_leasehold">
        <th>Is erfpachtcanon</th>
        <td>
          {{ property.is_leasehold_canon | toYesNo }}
        </td>
        <th v-if="property.is_leasehold_canon">Erfpachtcanon</th>
        <td v-if="property.is_leasehold_canon">
          {{ property.leasehold_canon | toCurrency }}
        </td>
      </tr>
      <tr v-if="property.is_leasehold">
        <th>Erfpachtcanon in verleden afgekocht</th>
        <td>
          {{ property.is_leasehold_canon_bought_off_in_past | toYesNo }}
        </td>
        <th>Erfpachtcanon eeuwig durend afgekocht</th>
        <td>
          {{ property.is_leasehold_canon_bought_off_forever | toYesNo }}
        </td>
      </tr>
      <tr v-if="property.is_leasehold_canon">
        <th>Erfpachtcanon afgekocht tot</th>
        <td>
          {{ property.leasehold_canon_bought_off_until_date | toDate }}
        </td>
      </tr>
      <tr>
        <td colspan="4">
          <h4>Marktinformatie</h4>
        </td>
      </tr>
      <tr>
        <th>Marktwaarde</th>
        <td>
          {{ property.market_value | toCurrency }}
        </td>
        <th>Verkoopprijs</th>
        <td>
          {{ property.sale_price  | toCurrency }}
        </td>
      </tr>
      <tr>
        <th>Verkoopstatus</th>
        <td>
          {{ property.sale_status }}
        </td>
        <th>Staat te koop</th>
        <td>
          {{ property.for_sale | toYesNo }}
        </td>
      </tr>
      <tr>
        <th>Verkocht</th>
        <td>
          {{ property.sold | toYesNo }}
        </td>
        <th>Prijs</th>
        <td>
          {{ property.price | toCurrency }}
        </td>
      </tr>
      <tr>
        <th>Prijs type</th>
        <td>
          {{ property.price_type }}
        </td>
        <th>Prijs soort</th>
        <td>
          {{ property.price_kind }}
        </td>
      </tr>
      <tr>
        <th>Waarde Laag</th>
        <td>
          {{ property.value_low | toCurrency }}
        </td>
        <th>Waarde hoog</th>
        <td>
          {{ property.value_high | toCurrency }}
        </td>
      </tr>
      <tr>
        <th>Kwaliteit waarde</th>
        <td>
          {{ property.value_quality }}
        </td>
        <th>Koopprijs</th>
        <td>
          {{ property.purchase_price | toCurrency }}
        </td>
      </tr>
      <tr>
      <tr>
        <th>WOZ-waarde</th>
        <td>
          {{ property.real_estate_value | toCurrency }}
        </td>
        <th>Originele schuld-marktwaarde verhouding</th>
        <td>
          {{ property.debt_market_value_ratio_original | toPercentage }}
        </td>
      </tr>
      <tr>
        <td colspan="4">
          Overig
        </td>
      </tr>
      <tr>
        <th>Soort</th>
        <td>
          {{ typeDescription }}
        </td>
        <th>Heeft appartementsrecht</th>
        <td>
          {{ property.has_apartment_right | toYesNo }}
        </td>
      </tr>
      <tr>
        <th>Verhuurtype</th>
        <td>
          {{ rentalTypeDescription }}
        </td>
        <th>Onderpandtype</th>
        <td>
          {{ collateralTypeDescription }}
        </td>
      </tr>
      <tr v-if="isNew">
        <th>Bouwplannummer</th>
        <td>
          {{ property.construction_plan_number }}
        </td>
        <th>Situatienummer</th>
        <td>
          {{ property.situation_number }}
        </td>
      </tr>
      <tr>
        <th>Appartementstype</th>
        <td>
          {{ property.apartment_type }}
        </td>
        <th>Appartement subtype</th>
        <td>
          {{ property.apartment_sub_type }}
        </td>
      </tr>
      <tr>
        <th>Huurinkomsten</th>
        <td>
          {{ property.rental_income | toCurrency }}
        </td>
      </tr>
      <tr>
        <th>Afvalstoffenheffing</th>
        <td>
          {{ property.waste_charges | toCurrency }}
        </td>
        <th>Rioolheffing</th>
        <td>
          {{ property.sewage_charges | toCurrency }}
        </td>
      </tr>
      <tr>
        <th>Label klasse</th>
        <td>
          {{ property.label_class }}
        </td>
        <th>Label type</th>
        <td>
          {{ property.label_type }}
        </td>
      </tr>
      <tr>
        <th>Prestatie index</th>
        <td>
          {{ property.performance_index }}
        </td>
        <th>Prestatie coëfficiënt</th>
        <td>
          {{ property.performance_coefficient }}
        </td>
      </tr>
      <tr>
        <th>Prestatie geldig tot</th>
        <td>
          {{ property.performance_valid_until | toDate }}
        </td>
      </tr>
      <tr v-if="hasValuations">
        <th colspan="4">
          Waarderingen
        </th>
      </tr>
      </tbody>
      <PropertyShowValuationComponent v-for="valuation in valuations" v-if="!compact" :key="valuation.id"
                                      :valuation="valuation"/>
      <tfoot v-if="edit">
      <tr>
        <th class="text-right" colspan="4">
          <a-button v-on:click="handleShowEditDrawer">
            Pand bewerken
          </a-button>
        </th>
      </tr>
      </tfoot>
    </table>
  </div>
  <div v-else>
    <span class="no-address">
      Er is geen adres gekoppeld.
    </span>
  </div>
</template>

<script>
import countryApi from '~/api/country';
import propertyApi from "~/api/property"
import PropertyEditDrawerComponent from './EditDrawerComponent'
import PropertyShowValuationComponent from '~/modules/property_valuation/components/ShowComponent'

export default {
  name: "PropertyShowComponent",
  components: {
    PropertyEditDrawerComponent,
    PropertyShowValuationComponent
  },
  props: {
    propertyObject: {
      type: Object,
      required: false,
      default: null
    },
    edit: {
      type: Boolean,
      required: false,
      default: true
    },
    propertyId: {
      required: false,
      default: null
    },
    compact: {
      type: Boolean,
      required: false,
      default: true
    },
  },
  data() {
    return {
      property: this.propertyObject,
      showEditDrawer: false,
      types: {
        single_family_home: 'Eengezinswoning',
        single_family_house_with_garage: 'Eengezinswoning met garage',
        flat_apartment: 'Flat/appartement',
        flat_apartment_with_garage: 'Flat/appartement met garage',
        shop_house: 'Winkel-woonhuis',
        residential_house_with_commercial_space: 'Woonhuis met bedrijfsruimte',
        store: 'Winkel',
        business_premises: 'Bedrijfspand',
        recreational_home: 'Recreatiewoning',
        farm: 'Boerderij',
        business_collateral: 'Zakelijk onderpand',
        agricultural_building_land: 'Agrarische grond/bouwgrond',
        garage: 'Garage',
        building_plot: 'Bouwkavel',
        houseboat: 'Woonschip',
        pleasure_craft: 'Pleziervaartuig',
      },
      rentalTypes: {
        no_rental: 'geen verhuur',
        temporary_rental: 'tijdelijke verhuur',
        partial_temporary_rental: 'gedeeltelijke tijdelijke verhuur (kamer, etage)',
        partial_rental: 'gedeeltelijke verhuur (kamer, etage)',
        permanent_rental: 'permanente verhuur',
      },
      collateralTypes: {
        existing: 'bestaand',
        existing_renovation: 'bestaand verbouw',
        new: 'nieuw',
      },
      occupancyTypes: {
        rental: 'Huur',
        resident: 'Inwonend',
        property: 'Eigendom',
      },
      loaded: false
    };
  },
  mounted() {
    if (this.propertyId !== null && this.propertyId !== '' && this.property === null) {
      propertyApi.findById(this.propertyId, ['valuations']).then(response => {
        this.property = response.data;
        this.loaded = true;
      });
    }
  },
  computed: {
    hasProperty() {
      return this.property !== null;
    },
    hasValuations() {
      return this.valuations.length > 0;
    },
    valuations() {
      if (typeof this.property.valuations !== "undefined") {
        return this.property.valuations.data;
      }
      return [];
    },
    countryName() {
      if (this.property.country && this.property.country.data) {
        return this.property.country.data.name
      } else if (this.property.country_id !== null) {
        countryApi.getById(this.property.country_id).then(response => {
          this.$set(this.property, 'country', response);
        });
      }
      return '';
    },
    typeDescription() {
      return this.types[this.property.type] ? this.types[this.property.type] : '';
    },
    rentalTypeDescription() {
      return this.rentalTypes[this.property.rental_type] ? this.rentalTypes[this.property.rental_type] : '';
    },
    collateralTypeDescription() {
      return this.collateralTypes[this.property.collateral_type] ? this.collateralTypes[this.property.collateral_type] : '';
    },
    isNew() {
      if (this.property && typeof this.property !== 'undefined' && this.property.collateral_type !== this.isNewProxy) {
        return this.property.collateral_type === "new";
      }
      return this.isNewProxy
    },
  },
  methods: {
    handleShowEditDrawer() {
      this.$refs.editDrawer.visible = true;
    },
    handlePropertyUpdated(property) {
      this.property = property;
      this.$emit('updated', property)
    },
  }
}
</script>

<style scoped>
.no-address {
  padding: 1rem;
}
</style>
